import { hasPermission } from 'core/auth/rbac'
import { RupeeIcon } from 'core/components/Icons'
import { Badge, BadgeTone } from 'core/components/ui/Badge'
import { Column, Table } from 'core/components/ui/Table'
import { formatDateTime } from 'core/utils/format'
import { useAuth } from 'features/auth/presentation/context/AuthContext'
import React from 'react'
import { OrderListItem, OrderStatus, formatOrderAddress } from '../../domain/entities/Order'

interface IndentsTableProps {
  orders: OrderListItem[]
  loading: boolean
  onReview: (order: OrderListItem) => void
  onPlaceOrder: (order: OrderListItem) => void
  canPlaceOrder?: boolean
  selectedIds?: Set<string>
  activeCatalogItemId?: string
  onToggleSelect?: (order: OrderListItem) => void
  onToggleSelectAll?: () => void
  currentPage: number
  totalPages: number
  onPageChange: (page: number) => void
  totalItems: number
  itemsPerPage: number
  emptyMessage?: string
}

const statusTones: Record<OrderStatus, BadgeTone> = {
  indent_raised: 'warning',
  order_placed: 'info',
  payment_received: 'info',
  order_confirmed: 'success',
  in_progress: 'secondary',
  dispatched: 'secondary',
  delivered: 'success',
}

const paymentStatusTones: Record<string, BadgeTone> = {
  pending: 'warning',
  uploaded: 'info',
  verified: 'success',
  rejected: 'danger',
}

interface TableCheckboxProps {
  checked: boolean
  indeterminate?: boolean
  disabled?: boolean
  title?: string
  onChange: () => void
  'aria-label'?: string
}

const getAriaChecked = (checked: boolean, indeterminate: boolean): 'true' | 'mixed' | 'false' => {
  if (checked) {
    return 'true'
  }

  if (indeterminate) {
    return 'mixed'
  }

  return 'false'
}

const getCheckboxStateClass = (disabled: boolean, isActive: boolean): string => {
  if (disabled) {
    return 'opacity-30 cursor-not-allowed bg-muted border-border'
  }

  if (isActive) {
    return 'bg-primary border-primary text-primary-fg cursor-pointer'
  }

  return 'bg-white border-border hover:border-primary/60 dark:bg-card dark:border-white/20 dark:hover:border-white/40 cursor-pointer'
}

const getClubButtonTitle = (isMismatch: boolean, isSelected: boolean): string => {
  if (isMismatch) {
    return 'Cannot club indents from different products'
  }

  if (isSelected) {
    return 'Remove from club selection'
  }

  return 'Add to club selection'
}

const TableCheckbox: React.FC<TableCheckboxProps> = ({
  checked,
  indeterminate = false,
  disabled = false,
  title,
  onChange,
  'aria-label': ariaLabel,
}) => {
  const isActive = checked || indeterminate

  const renderCheckboxIcon = () => {
    if (checked) {
      return (
        <svg className="w-2.5 h-2.5 stroke-[3.5]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
        </svg>
      )
    }

    if (indeterminate) {
      return (
        <svg className="w-2.5 h-2.5 stroke-[3.5]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 12h14" />
        </svg>
      )
    }

    return null
  }

  return (
    <button
      type="button"
      role="checkbox"
      disabled={disabled}
      title={title}
      aria-checked={getAriaChecked(checked, indeterminate)}
      aria-label={ariaLabel}
      onClick={(e) => {
        e.stopPropagation()
        if (!disabled) onChange()
      }}
      className={`h-4 w-4 rounded flex items-center justify-center transition-colors border focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 ${getCheckboxStateClass(disabled, isActive)}`}
    >
      {renderCheckboxIcon()}
    </button>
  )
}

export const IndentsTable: React.FC<IndentsTableProps> = ({
  orders,
  loading,
  onReview,
  onPlaceOrder,
  canPlaceOrder: canPlaceOrderProp,
  selectedIds,
  activeCatalogItemId,
  onToggleSelect,
  onToggleSelectAll,
  currentPage,
  totalPages,
  onPageChange,
  totalItems,
  itemsPerPage,
  emptyMessage,
}) => {
  const { user } = useAuth()
  const canPlaceOrder = canPlaceOrderProp !== undefined ? canPlaceOrderProp : hasPermission(user, 'update_order_status')

  const matchingOrders = activeCatalogItemId
    ? orders.filter((o) => o.items?.[0]?.catalogItemId === activeCatalogItemId)
    : orders
  const numSelected = matchingOrders.filter((o) => selectedIds?.has(o.id)).length
  const allSelected = matchingOrders.length > 0 && numSelected === matchingOrders.length
  const isIndeterminate = numSelected > 0 && !allSelected

  const columns: Column<OrderListItem>[] = [
    ...(canPlaceOrder
      ? [
          {
            key: 'select',
            label: (
              <TableCheckbox
                checked={allSelected}
                indeterminate={isIndeterminate}
                onChange={() => onToggleSelectAll?.()}
                aria-label="Select all orders"
                title={
                  activeCatalogItemId
                    ? 'Select/deselect all matching product indents on this page'
                    : 'Select all indents for the first product on this page'
                }
              />
            ),
            render: (row: OrderListItem) => {
              const rowCatId = row.items?.[0]?.catalogItemId
              const isMismatch = Boolean(activeCatalogItemId && rowCatId && rowCatId !== activeCatalogItemId)
              const isChecked = selectedIds?.has(row.id) || false

              return (
                <TableCheckbox
                  checked={isChecked}
                  disabled={isMismatch}
                  title={
                    isMismatch ? 'Indents must belong to the same catalog product to be clubbed together' : undefined
                  }
                  onChange={() => onToggleSelect?.(row)}
                  aria-label={`Select order ${row.id}`}
                />
              )
            },
          },
        ]
      : []),
    {
      key: 'id',
      label: 'Order ID',
      className: 'font-semibold text-text-primary',
    },
    {
      key: 'product',
      label: 'Product Name',
      render: (row) => {
        const item = row.items?.[0]
        if (!item?.productName) {
          return <span className="text-text-secondary text-xs">—</span>
        }

        return (
          <div className="flex flex-col gap-0.5 max-w-[200px]" title={item.productName}>
            <span className="font-semibold text-text-primary text-xs leading-tight truncate">{item.productName}</span>
            {(item.variant || item.unit) && (
              <span className="text-[10px] text-text-secondary font-medium truncate">
                {[item.variant, item.unit].filter(Boolean).join(' • ')}
              </span>
            )}
          </div>
        )
      },
    },
    {
      key: 'units',
      label: 'Units Qty',
      render: (row) => (
        <span className="text-xs font-bold text-text-primary">
          {row.totalUnits !== undefined ? `${row.totalUnits} Units` : '—'}
        </span>
      ),
    },
    {
      key: 'createdAt',
      label: 'Created At',
      render: (row) => (
        <span className="text-xs text-text-primary font-medium whitespace-nowrap">
          {formatDateTime(row.orderPlacedDate || row.createdAt)}
        </span>
      ),
    },
    {
      key: 'buyer',
      label: 'Buyer Info',
      render: (row) => (
        <div className="flex flex-col gap-1">
          <span className="font-semibold text-text-primary leading-tight">
            {row.buyer?.businessName || row.buyer?.name || '—'}
          </span>
          <div className="flex items-center gap-1.5">
            {row.buyer?.role && (
              <Badge tone="neutral" variant="outline" className="capitalize text-[10px] px-1 py-0 font-bold shrink-0">
                {row.buyer.role}
              </Badge>
            )}
            <span className="text-[10px] text-text-secondary font-medium">{row.buyer?.phone}</span>
          </div>
        </div>
      ),
    },
    {
      key: 'shipToAddress',
      label: 'Order Address',
      className: 'text-text-secondary text-xs max-w-[220px] truncate',
      render: (row) => {
        const address = formatOrderAddress(row.shipToAddress)
        return <span title={address}>{address}</span>
      },
    },
    {
      key: 'payment',
      label: 'Payment Status',
      render: (row) => (
        <div className="flex items-center gap-2">
          <Badge tone={paymentStatusTones[row.paymentStatus] || 'neutral'} variant="subtle" className="capitalize">
            {row.paymentStatus || 'Awaiting Payment'}
          </Badge>
          <span className="text-xs text-text-secondary font-semibold uppercase">{row.paymentMode}</span>
        </div>
      ),
    },
    {
      key: 'status',
      label: 'Order Status',
      render: (row) => (
        <Badge tone={statusTones[row.status]} variant="subtle" className="capitalize">
          {row.status.replace(/_/g, ' ')}
        </Badge>
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      className: 'text-center',
      render: (row) => {
        const rowCatId = row.items?.[0]?.catalogItemId
        const isMismatch = Boolean(activeCatalogItemId && rowCatId && rowCatId !== activeCatalogItemId)
        const isSelected = selectedIds?.has(row.id) || false

        return (
          <div className="flex items-center justify-center gap-2">
            <button
              onClick={() => onReview(row)}
              className="p-1.5 rounded-btn hover:bg-bg text-text-secondary hover:text-text-primary transition-colors text-xs font-semibold flex items-center gap-1 border border-border/50 hover:border-border dark:border-[rgba(255,255,255,0.14)] dark:hover:border-[rgba(255,255,255,0.28)]"
            >
              <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2.5"
                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                />
              </svg>
              Review Details
            </button>
            {canPlaceOrder && (
              <button
                onClick={() => onPlaceOrder(row)}
                disabled={isMismatch}
                title={getClubButtonTitle(isMismatch, isSelected)}
                className={`p-1.5 rounded-btn transition-colors text-xs font-semibold flex items-center gap-1 border ${
                  isSelected
                    ? 'bg-primary/10 border-primary text-primary font-bold'
                    : 'border-border/50 hover:border-border text-text-secondary hover:text-text-primary hover:bg-bg dark:border-[rgba(255,255,255,0.14)] dark:hover:border-[rgba(255,255,255,0.28)] disabled:opacity-40 disabled:cursor-not-allowed'
                }`}
              >
                <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2.5"
                    d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
                  />
                </svg>
                {isSelected ? 'Selected' : 'Club'}
              </button>
            )}
          </div>
        )
      },
    },
  ]

  return (
    <Table
      columns={columns}
      data={orders}
      loading={loading}
      pagination={{
        currentPage,
        totalPages,
        onPageChange,
        totalItems,
        itemsPerPage,
      }}
      emptyMessage={emptyMessage || 'No indents match your query parameters.'}
      emptyIcon={<RupeeIcon size={20} className="opacity-60" />}
    />
  )
}
